import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A round-bellied baby dragon in the storybook style, sitting on its haunches
// like a puppy: a fat cream tummy, stubby little wings, two tiny bone-cream
// horns, an arrow-tipped tail curled on the ground and a huge head with big
// friendly eyes. It puffs a tiny flame — a wisp at rest that blooms into a
// proper puff in the one-shot "flame-puff".
//
// Frame: faces +Z (snout and eyes forward), sits on the floor (y = 0).
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"   : belly + tummy + spine ridges. Breathes about the haunches.
//   - "head"   : skull, snout, horns and eyes. Bobs and tips back to puff.
//   - "wing-l" / "wing-r" : the stubby wings, flapping about the shoulders.
//   - "tail"   : the curled tail with its arrow tip, wagging on the floor.
//   - "flame"  : a small flame cone at the snout; authored as a tiny wisp
//                (part scale 0.18) and bloomed by the flame-puff scale track.
// The haunches and front legs are static (unnamed) so the sit stays planted.

const SKIN = "#d95d4e";        // warm coral-red hide
const SKIN_DARK = "#b2443c";   // shaded red (wings, haunches, spine ridges)
const BELLY = "#f6efe3";       // cream tummy, chin and tail underside
const HORN = "#efe3c8";        // soft bone-cream horns
const EYE_WHITE = "#fdfaf2";   // warm white (not pure white)
const EYE_DARK = "#2e2a33";    // warm-dark pupil (not pure black)
const FLAME = "#f6a83c";       // warm orange flame puff

// ── frame heights ────────────────────────────────────────────────────────────
const BODY_CY = 0.26;          // belly centre
const NECK_Y = 0.40;           // where the head swivels
const HEAD_CY = 0.48;          // skull centre

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.12, 0];
const HEAD_PIVOT: [number, number, number] = [0, NECK_Y, 0.04];
const TAIL_PIVOT: [number, number, number] = [0, 0.10, -0.18];
const WING_L: [number, number, number] = [-0.13, 0.34, -0.05];
const WING_R: [number, number, number] = [0.13, 0.34, -0.05];
// The flame grows outward from the mouth, not from its own centre.
const FLAME_PIVOT: [number, number, number] = [0, 0.44, 0.20];

// One stubby wing: a round shoulder nub plus a flattened triangular fin
// leaning up, out and slightly back. Named so it flaps about the shoulder.
function wing(name: string, side: -1 | 1): Part[] {
  const [sx, sy, sz] = side < 0 ? WING_L : WING_R;
  return [
    // Shoulder nub seating the fin against the body.
    { geometry: new THREE.SphereGeometry(0.045, 10, 8), color: SKIN_DARK,
      position: [sx, sy, sz], name },
    // Fin: a flattened cone leaning out and swept back like a little bat wing.
    { geometry: new THREE.ConeGeometry(0.08, 0.20, 8), color: SKIN_DARK,
      position: [sx + side * 0.07, sy + 0.07, sz - 0.045],
      rotation: [-0.55, 0, side * -0.85], scale: [1, 1, 0.35], name },
  ];
}

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.05, 12, 10), color: EYE_WHITE, position: [x, HEAD_CY + 0.045, 0.115], name: "head" },
    { geometry: new THREE.SphereGeometry(0.027, 10, 8), color: EYE_DARK, position: [x * 0.92, HEAD_CY + 0.045, 0.153], name: "head" },
    { geometry: new THREE.SphereGeometry(0.009, 8, 6), color: EYE_WHITE, position: [x * 0.92 - 0.010, HEAD_CY + 0.058, 0.172], name: "head" },
  ];
}

export const babyDragon = defineModel({
  id: "baby-dragon",
  batchId: "mess",
  name: "Baby Dragon",
  tags: ["mess", "cute", "animated", "creature", "dragon", "fantasy", "baby", "toy"],
  summary: "A round-bellied baby dragon sitting like a puppy, with stubby wings and a tiny flame puff.",
  description:
    "A lovable baby dragon sitting on its haunches: a fat cream tummy, stubby " +
    "flappable wings, two tiny bone-cream horns, an arrow-tipped tail curled on " +
    "the ground and a huge head with big friendly eyes. The idle breathes and " +
    "wags; the wings flap; and a one-shot flame-puff tips the head back and " +
    "blooms a little orange flame from the snout.",
  author: "models",
  footprint: 0.38,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── haunches: two flattened thigh blobs splayed at the floor ─────────
      { geometry: new THREE.SphereGeometry(0.115, 12, 10), color: SKIN_DARK,
        position: [-0.155, 0.10, 0.0], scale: [0.75, 0.85, 1.1] },
      { geometry: new THREE.SphereGeometry(0.115, 12, 10), color: SKIN_DARK,
        position: [0.155, 0.10, 0.0], scale: [0.75, 0.85, 1.1] },
      // Big back feet poking forward from under the haunches.
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: SKIN,
        position: [-0.155, 0.035, 0.12], scale: [1.0, 0.6, 1.3] },
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: SKIN,
        position: [0.155, 0.035, 0.12], scale: [1.0, 0.6, 1.3] },
      // Two little front legs propping the chest, puppy-style.
      { geometry: new THREE.CylinderGeometry(0.032, 0.038, 0.18, 8), color: SKIN,
        position: [-0.085, 0.10, 0.135] },
      { geometry: new THREE.CylinderGeometry(0.032, 0.038, 0.18, 8), color: SKIN,
        position: [0.085, 0.10, 0.135] },
      { geometry: new THREE.SphereGeometry(0.045, 10, 8), color: SKIN_DARK,
        position: [-0.085, 0.03, 0.16], scale: [1.0, 0.6, 1.25] },
      { geometry: new THREE.SphereGeometry(0.045, 10, 8), color: SKIN_DARK,
        position: [0.085, 0.03, 0.16], scale: [1.0, 0.6, 1.25] },

      // ── body: a fat round belly leaning back on the haunches ─────────────
      { geometry: new THREE.SphereGeometry(0.195, 14, 12), color: SKIN,
        position: [0, BODY_CY, -0.02], scale: [1.0, 1.08, 0.95], name: "body" },
      // Cream tummy bulging forward.
      { geometry: new THREE.SphereGeometry(0.145, 12, 10), color: BELLY,
        position: [0, BODY_CY - 0.02, 0.08], scale: [0.9, 1.0, 0.75], name: "body" },
      // Three little spine ridges hugging the curve of the back.
      { geometry: new THREE.ConeGeometry(0.028, 0.06, 6), color: SKIN_DARK,
        position: [0, 0.41, -0.13], rotation: [-0.6, 0, 0], name: "body" },
      { geometry: new THREE.ConeGeometry(0.025, 0.055, 6), color: SKIN_DARK,
        position: [0, 0.34, -0.175], rotation: [-0.95, 0, 0], name: "body" },
      { geometry: new THREE.ConeGeometry(0.022, 0.05, 6), color: SKIN_DARK,
        position: [0, 0.26, -0.195], rotation: [-1.3, 0, 0], name: "body" },

      // ── head: a huge round skull with a blunt snout ──────────────────────
      { geometry: new THREE.SphereGeometry(0.155, 14, 12), color: SKIN,
        position: [0, HEAD_CY, 0.03], name: "head" },
      // Blunt snout with a cream chin under it.
      { geometry: new THREE.SphereGeometry(0.085, 12, 10), color: SKIN,
        position: [0, HEAD_CY - 0.035, 0.15], scale: [1.05, 0.65, 1.0], name: "head" },
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: BELLY,
        position: [0, HEAD_CY - 0.07, 0.135], scale: [1.05, 0.5, 0.95], name: "head" },
      // Two tiny nostril dots on the snout tip.
      { geometry: new THREE.SphereGeometry(0.010, 6, 5), color: SKIN_DARK, position: [-0.032, HEAD_CY - 0.015, 0.232], name: "head" },
      { geometry: new THREE.SphereGeometry(0.010, 6, 5), color: SKIN_DARK, position: [0.032, HEAD_CY - 0.015, 0.232], name: "head" },
      // Two tiny bone-cream horns on the crown, tipped outward.
      { geometry: new THREE.ConeGeometry(0.028, 0.09, 8), color: HORN,
        position: [-0.065, HEAD_CY + 0.155, -0.02], rotation: [-0.15, 0, -0.35], name: "head" },
      { geometry: new THREE.ConeGeometry(0.028, 0.09, 8), color: HORN,
        position: [0.065, HEAD_CY + 0.155, -0.02], rotation: [-0.15, 0, 0.35], name: "head" },
      ...eye(-0.068),
      ...eye(0.068),

      // ── flame: a tiny wisp at the snout tip, bloomed by "flame-puff" ─────
      { geometry: new THREE.ConeGeometry(0.05, 0.14, 8), color: FLAME,
        position: [0, 0.44, 0.245], rotation: [Math.PI / 2, 0, 0], scale: 0.18, name: "flame" },

      // ── wings: stubby, flappable ─────────────────────────────────────────
      ...wing("wing-l", -1),
      ...wing("wing-r", 1),

      // ── tail: curled on the floor, ending in an arrow tip ────────────────
      { geometry: new THREE.CylinderGeometry(0.032, 0.085, 0.24, 10), color: SKIN,
        position: [0.04, 0.075, -0.24], rotation: [-1.3, -0.35, 0], name: "tail" },
      { geometry: new THREE.ConeGeometry(0.05, 0.095, 4), color: SKIN_DARK,
        position: [0.05, 0.10, -0.39], rotation: [-Math.PI / 2, 0, 0], scale: [0.35, 1.0, 1.1], name: "tail" },
    ];
  },
  animations: [
    // 1. Idle — a soft puppy breathe: the belly rises, the head bobs, the wings
    //    settle gently and the tail tip wags on the floor.
    {
      id: "idle",
      label: "Idle breathe",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.016, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.02, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.4, value: [-0.04, 0, 0], ease: "sine" },
            { t: 2.8, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "wing-l", pivot: WING_L,
          rotation: [
            { t: 0, value: [0, 0, -0.08], ease: "sine" },
            { t: 1.4, value: [0, 0, 0.10], ease: "sine" },
            { t: 2.8, value: [0, 0, -0.08], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R,
          rotation: [
            { t: 0, value: [0, 0, 0.08], ease: "sine" },
            { t: 1.4, value: [0, 0, -0.10], ease: "sine" },
            { t: 2.8, value: [0, 0, 0.08], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.18, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.18, 0], ease: "sine" },
            { t: 2.8, value: [0, -0.18, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Flap — the signature move: both stubby wings beat hard while the body
    //    bounces hopefully (it cannot fly yet, but it tries) and the tail wags.
    {
      id: "flap",
      label: "Wing flap",
      duration: 1.6,
      loop: true,
      tracks: [
        {
          target: "wing-l", pivot: WING_L,
          rotation: [
            { t: 0, value: [0, 0, -0.4], ease: "easeInOut" },
            { t: 0.4, value: [0, 0, 0.9], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, -0.4], ease: "easeInOut" },
            { t: 1.2, value: [0, 0, 0.9], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, -0.4], ease: "easeInOut" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R,
          rotation: [
            { t: 0, value: [0, 0, 0.4], ease: "easeInOut" },
            { t: 0.4, value: [0, 0, -0.9], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0.4], ease: "easeInOut" },
            { t: 1.2, value: [0, 0, -0.9], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0.4], ease: "easeInOut" },
          ],
        },
        // The whole body bounces with each beat.
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.025, 0], ease: "sine" },
            { t: 0.8, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.025, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.03, 0], ease: "sine" },
            { t: 0.8, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.03, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.25, 0], ease: "sine" },
            { t: 0.8, value: [0, 0.25, 0], ease: "sine" },
            { t: 1.6, value: [0, -0.25, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 3. Flame puff — a one-shot: the head rears back, then tips forward as the
    //    flame wisp blooms into a proper puff and shrinks away again. The wings
    //    give one startled flick. loop: false.
    {
      id: "flame-puff",
      label: "Flame puff",
      duration: 1.4,
      loop: false,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [-0.4, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0.22, 0, 0], ease: "easeIn" },
            { t: 1.0, value: [0.22, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        // The flame blooms from its wisp (authored part scale 0.18) to a full
        // puff (node scale 6 = ~1:1 geometry) and back down.
        {
          target: "flame", pivot: FLAME_PIVOT,
          scale: [
            { t: 0, value: 0.05, ease: "easeOut" },
            { t: 0.6, value: 6.0, ease: "easeOut" },
            { t: 0.95, value: 5.0, ease: "easeInOut" },
            { t: 1.4, value: 0.05, ease: "easeIn" },
          ],
        },
        // One startled wing flick as the puff goes off.
        {
          target: "wing-l", pivot: WING_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0, 0.8], ease: "easeOut" },
            { t: 1.1, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0] },
          ],
        },
        {
          target: "wing-r", pivot: WING_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0, -0.8], ease: "easeOut" },
            { t: 1.1, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0] },
          ],
        },
      ],
    },
  ],
});
